import React, { useEffect, useState } from "react";
import { Factory, Plus, Calendar, Brain, Trash2, Sparkles, ArrowLeftRight, BarChart2, Download, Edit, ShieldCheck, Printer, ClipboardList, ShoppingCart, Search, X, GripVertical } from "lucide-react";
import Pin2Prompt from "../components/ui/Pin2Prompt";
import api, { getPin2 } from "../lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input, Label, Select, Textarea } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { toast } from "../components/ui/Toast";
import { useI18n } from "../contexts/I18nContext";
import { formatDate, formatDateTime, cn, formatVND, parseApiError} from "../lib/utils";

const SHIFTS = [
  { id: "morning", label: "Sáng (6h-11h)" },
  { id: "afternoon", label: "Chiều (11h-17h)" },
  { id: "evening", label: "Tối (17h-22h)" },
  { id: "night", label: "Đêm" },
];

const stripAccents = (s) => {
  if (!s) return "";
  return String(s).toLowerCase().replace(/đ/g, "d").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

export default function Production() {
  const { t } = useI18n();
  const [batches, setBatches] = useState([]);
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("xnt");
  // Stock movements tab state
  const [movements, setMovements] = useState([]);
  // PIN2 + edit movement
  const [pin2Open, setPin2Open] = useState(false);
  const [pendingMovEdit, setPendingMovEdit] = useState(null);
  const [editMovModal, setEditMovModal] = useState(null);
  const [editMovForm, setEditMovForm] = useState({});
  const [editMovSubmitting, setEditMovSubmitting] = useState(false);
  const [movKind, setMovKind] = useState("");
  const [movDateFrom, setMovDateFrom] = useState(() => (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })());
  const [movDateTo, setMovDateTo] = useState(() => (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })());
  // XNT report state
  const [xntKind, setXntKind] = useState("product");
  // Kế hoạch sản xuất
  const [planKind, setPlanKind] = useState("product"); // product | material
  const [xntDateFrom, setXntDateFrom] = useState(() => (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })());
  const [xntDateTo, setXntDateTo] = useState(() => (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })());
  const [xnt, setXnt] = useState(null);
  const [xntSearch, setXntSearch] = useState("");   // tìm kiếm trong bảng XNT
  const [movSearch, setMovSearch] = useState("");    // tìm kiếm trong lịch sử
  // Drag & drop order cho bảng XNT
  const [xntOrder, setXntOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem("xnt_row_order") || "null"); } catch { return null; }
  });
  const [dragSrcIdx, setDragSrcIdx] = useState(null);
  const [form, setForm] = useState({
    product_id: "",
    quantity: 1,
    shift: "morning",
    production_date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })(),
    expiration_date: "",
    batch_code: "",
    materials_used: [],
    notes: "",
  });
  const [recipePreview, setRecipePreview] = useState(null); // {estimate: [...]}
  const [recipePreviewLoading, setRecipePreviewLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [recipes, setRecipes] = useState({}); // {product_id: [{material_id, quantity, unit}]}
  const [planSubTab, setPlanSubTab] = useState("product"); // product | material_purchase
  // Kế hoạch mua NVL: số lượng SP dự kiến SX (người dùng tự nhập)
  const [planAllocation, setPlanAllocation] = useState({}); // { product_id: qty }

  const load = async () => {
    try {
      const [b, p, m] = await Promise.all([
        api.get("/production"),
        api.get("/products"),
        api.get("/materials").catch(() => ({ data: [] })),
      ]);
      setBatches(b.data);
      setProducts(p.data);
      setMaterials(m.data);
      // Load all recipes
      try {
        const rec = await api.get("/products/recipes/all");
        setRecipes(rec.data || {});
      } catch { setRecipes({}); }
    } finally {
      setDataLoaded(true);
    }
  };

  const loadForecast = async () => {
    try {
      const { data } = await api.get("/production/forecast?days_ahead=1");
      setForecast(data);
    } catch {}
  };

  const loadMovements = async () => {
    const params = new URLSearchParams();
    if (movKind) params.set("kind", movKind);
    if (movDateFrom) params.set("date_from", movDateFrom);
    if (movDateTo) params.set("date_to", movDateTo);
    const { data } = await api.get(`/stock/movements?${params.toString()}`);
    setMovements(data);
  };

  const loadXNT = async () => {
    const params = new URLSearchParams({ kind: xntKind });
    if (xntDateFrom) params.set("date_from", xntDateFrom);
    if (xntDateTo) params.set("date_to", xntDateTo);
    const { data } = await api.get(`/inventory/xnt?${params.toString()}`);
    setXnt(data);
  };

  const exportXntXlsx = () => {
    if (!xnt || !xnt.items?.length) return;
    // Build CSV (Excel-compatible) with BOM for VN diacritics
    const rows = [
      ["Tên", "ĐVT", "Tồn đầu", "Nhập", "Xuất", "Tồn cuối"],
      ...xnt.items.map((r) => [r.name, r.unit, r.opening_stock, r.total_in, r.total_out, r.ending_stock]),
      ["TỔNG", "", xnt.totals.total_opening, xnt.totals.total_in, xnt.totals.total_out, xnt.totals.total_ending],
    ];
    const csv = "\uFEFF" + rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `xnt_${xntKind}_${xntDateFrom}_${xntDateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printXNT = () => {
    if (!xnt || !xnt.items?.length) return;
    const style = `<style>body{font-family:Arial,sans-serif;font-size:12px;margin:20px}h2{margin:0 0 4px}p.sub{color:#555;font-size:11px;margin:0 0 12px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:5px 8px}th{background:#f0f0f0;font-size:11px;text-transform:uppercase;text-align:left}tfoot td{font-weight:bold}.in{color:#15803d}.out{color:#b91c1c}@media print{body{margin:10mm}}</style>`;
    const rows = xnt.items.map((r, i) =>
      `<tr><td>${i+1}</td><td>${r.name}</td><td>${r.unit}</td><td style="text-align:right">${r.opening_stock}</td><td style="text-align:right" class="in">+${r.total_in}</td><td style="text-align:right" class="out">-${r.total_out}</td><td style="text-align:right;font-weight:bold">${r.ending_stock}</td></tr>`
    ).join("");
    const html = `<!DOCTYPE html><html lang="vi"><head><meta charset="UTF-8"/><title>Báo cáo XNT</title>${style}</head><body>
    <h2>Báo cáo Xuất Nhập Tồn — ${xntKind === "product" ? "Sản phẩm" : "Nguyên vật liệu"}</h2>
    <p class="sub">Từ ${xntDateFrom} đến ${xntDateTo} · ${xnt.items.length} mục · In ngày ${new Date().toLocaleDateString("vi-VN")}</p>
    <table><thead><tr><th>STT</th><th>Tên</th><th>ĐVT</th><th style="text-align:right">Tồn đầu</th><th style="text-align:right" class="in">Nhập</th><th style="text-align:right" class="out">Xuất</th><th style="text-align:right">Tồn cuối</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr><td colspan="3" style="text-align:right">TỔNG</td><td style="text-align:right">${xnt.totals.total_opening}</td><td style="text-align:right" class="in">+${xnt.totals.total_in}</td><td style="text-align:right" class="out">-${xnt.totals.total_out}</td><td style="text-align:right">${xnt.totals.total_ending}</td></tr></tfoot>
    </table><script>window.onload=()=>{window.print()}<\/script></body></html>`;
    const win = window.open("", "_blank"); win.document.write(html); win.document.close();
  };

  const printMovements = () => {
    if (!movements.length) return;
    const style = `<style>body{font-family:Arial,sans-serif;font-size:12px;margin:20px}h2{margin:0 0 4px}p.sub{color:#555;font-size:11px;margin:0 0 12px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:5px 8px}th{background:#f0f0f0;font-size:11px;text-transform:uppercase;text-align:left}.in{color:#15803d}.out{color:#b91c1c}@media print{body{margin:10mm}}</style>`;
    const rows = movements.map((m, i) => {
      const isIn = (m.quantity_change || 0) > 0;
      return `<tr><td>${i+1}</td><td>${m.created_at ? m.created_at.slice(0,16).replace("T"," ") : "—"}</td><td>${m.kind === "product" ? "SP" : "NVL"}</td><td>${m.target_name || ""}</td><td class="${isIn?"in":"out"}">${isIn?"+":""}${m.quantity_change}</td><td style="text-align:right">${m.unit_price ? m.unit_price.toLocaleString("vi-VN")+"₫" : "—"}</td><td>${m.batch_code||""}</td><td>${m.expiration_date ? m.expiration_date.slice(0,10) : ""}</td><td>${m.notes||""}</td></tr>`;
    }).join("");
    const html = `<!DOCTYPE html><html lang="vi"><head><meta charset="UTF-8"/><title>Lịch sử xuất nhập</title>${style}</head><body>
    <h2>Lịch sử xuất nhập kho</h2>
    <p class="sub">${movements.length} phiếu · In ngày ${new Date().toLocaleDateString("vi-VN")}</p>
    <table><thead><tr><th>STT</th><th>Thời gian</th><th>Loại</th><th>Đối tượng</th><th>Số lượng</th><th style="text-align:right">Đơn giá</th><th>Mã lô</th><th>HSD</th><th>Ghi chú</th></tr></thead>
    <tbody>${rows}</tbody>
    </table><script>window.onload=()=>{window.print()}<\/script></body></html>`;
    const win = window.open("", "_blank"); win.document.write(html); win.document.close();
  };


  // ── Kế hoạch sản xuất ──────────────────────────────────────────────────────
  // Dùng tồn cuối kỳ (ending_stock) từ XNT, fallback về item.stock nếu chưa có XNT
  const planItems = (() => {
    const source = planKind === "product" ? products : materials;
    if (!source?.length) return [];

    // Tạo map ending_stock từ xnt theo id
    const endingMap = {};
    if (xnt?.items?.length) {
      for (const row of xnt.items) {
        endingMap[row.id] = row.ending_stock;
      }
    }

    return source
      .map((item) => {
        const idKey = planKind === "product" ? item.product_id : item.material_id;
        const threshold = item.low_stock_threshold || 0;
        // Ưu tiên ending_stock từ XNT, fallback về stock DB
        const currentStock = idKey in endingMap ? endingMap[idKey] : (item.stock || 0);
        const needed = Math.max(0, threshold - currentStock);
        return {
          id: idKey,
          name: item.name,
          unit: item.unit,
          threshold,
          current_stock: currentStock,
          needed,
          isLow: threshold > 0 && currentStock < threshold,
          stock_source: idKey in endingMap ? "xnt" : "db",
        };
      })
      .sort((a, b) => b.needed - a.needed);
  })();

  // ── Kế hoạch mua NVL ── Tổng hợp NVL từ số lượng SX kế hoạch người dùng nhập × recipe − tồn NVL
  const materialPurchasePlan = (() => {
    if (!products?.length || !materials?.length) return [];
    console.log("[NVL Plan] planAllocation:", planAllocation);
    console.log("[NVL Plan] recipes keys:", Object.keys(recipes));
    console.log("[NVL Plan] products sample:", products[0]);
    const acc = {}; // material_id -> {...}
    for (const p of products) {
      // Ưu tiên số người dùng nhập; nếu chưa nhập thì lấy từ kế hoạch SX (threshold - stock)
      const plannedQty = planAllocation[p.product_id] !== undefined
        ? Number(planAllocation[p.product_id] || 0)
        : (planItems.find(r => r.id === p.product_id)?.needed || 0);
      if (plannedQty <= 0) continue;
      const recipe = recipes[p.product_id] || p.recipe || [];
      for (const r of recipe) {
        if (!r.material_id || !r.quantity) continue;
        const mat = materials.find(m => m.material_id === r.material_id);
        if (!mat) continue;
        const qtyNeeded = r.quantity * plannedQty;
        if (!acc[r.material_id]) {
          // Lấy ending_stock của NVL từ xnt (nếu có), fallback DB
          const matEndingStock = (xnt?.items || []).find(x => x.id === r.material_id)?.ending_stock;
          acc[r.material_id] = {
            material_id: r.material_id,
            name: mat.name,
            unit: mat.unit || r.unit || "",
            current_stock: matEndingStock !== undefined ? matEndingStock : (mat.stock || 0),
            needed_for_production: 0,
            cost: mat.cost || 0,
            sources: [],
          };
        }
        acc[r.material_id].needed_for_production += qtyNeeded;
        acc[r.material_id].sources.push({
          product_name: p.name,
          planned_qty: plannedQty,
          qty_per_unit: r.quantity,
          total_qty: qtyNeeded,
        });
      }
    }
    return Object.values(acc)
      .map(item => ({
        ...item,
        must_buy: Math.max(0, item.needed_for_production - item.current_stock),
        total_cost: item.cost * Math.max(0, item.needed_for_production - item.current_stock),
      }))
      .sort((a, b) => b.must_buy - a.must_buy);
  })();

  const printMaterialPurchasePlan = () => {
    if (!materialPurchasePlan.length) return;
    const style = `<style>
      body{font-family:Arial,sans-serif;font-size:12px;margin:20px}
      h2{margin:0 0 4px}p.sub{color:#555;font-size:11px;margin:0 0 12px}
      table{width:100%;border-collapse:collapse}
      th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}
      th{background:#f0f0f0;font-size:11px;text-transform:uppercase}
      .need{color:#b91c1c;font-weight:bold}.ok{color:#15803d}
      tr:nth-child(even) td{background:#fafafa}
      tfoot td{font-weight:bold;background:#fef9c3}
      @media print{body{margin:10mm}}
    </style>`;
    const rows = materialPurchasePlan.map((r, i) => `<tr>
      <td>${i + 1}</td>
      <td>${r.name}</td>
      <td>${r.unit}</td>
      <td style="text-align:right">${r.needed_for_production}</td>
      <td style="text-align:right" class="${r.must_buy > 0 ? 'need' : 'ok'}">${r.must_buy > 0 ? '+' + r.must_buy : '✓ Đủ'}</td>
      <td style="text-align:right">${r.total_cost > 0 ? r.total_cost.toLocaleString('vi-VN') + '₫' : '—'}</td>
      <td>${r.sources.map(s => `${s.product_name}: ${s.qty_per_unit}×${s.planned_qty}=${s.total_qty}`).join('; ')}</td>
    </tr>`).join("");
    const totalBuy = materialPurchasePlan.reduce((s, r) => s + r.must_buy, 0);
    const totalCost = materialPurchasePlan.reduce((s, r) => s + r.total_cost, 0);
    const html = `<!DOCTYPE html><html lang="vi"><head><meta charset="UTF-8"/>
      <title>Kế hoạch mua NVL</title>${style}</head><body>
      <h2>Kế hoạch mua nguyên vật liệu</h2>
      <p class="sub">Dựa trên kế hoạch sản xuất · ${materialPurchasePlan.length} loại NVL · In ngày ${new Date().toLocaleDateString("vi-VN")}</p>
      <p class="sub" style="color:#b91c1c">Công thức: <strong>Cần mua = NVL cần cho SX − Tồn kho NVL hiện tại</strong></p>
      <table>
        <thead><tr>
          <th>STT</th><th>Tên NVL</th><th>ĐVT</th>
          <th style="text-align:right">Tồn hiện tại</th>
          <th style="text-align:right">Cần cho SX</th>
          <th style="text-align:right">Cần mua thêm</th>
          <th style="text-align:right">Dự tính chi phí</th>
          <th>Ghi chú</th>
        </tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr>
          <td colspan="5" style="text-align:right">TỔNG</td>
          <td style="text-align:right;color:#b91c1c">+${totalBuy}</td>
          <td style="text-align:right">${totalCost > 0 ? totalCost.toLocaleString('vi-VN') + '₫' : '—'}</td>
          <td></td>
        </tr></tfoot>
      </table>
      <script>window.onload=()=>{window.print()}<\/script>
    </body></html>`;
    const win = window.open("", "_blank"); win.document.write(html); win.document.close();
  };

    const printPlan = () => {
    if (!planItems.length) return;
    const kindLabel = planKind === "product" ? "Sản phẩm" : "Nguyên vật liệu";
    const dateRange = `${xntDateFrom} → ${xntDateTo}`;
    const style = `<style>
      body{font-family:Arial,sans-serif;font-size:12px;margin:20px}
      h2{margin:0 0 4px}p.sub{color:#555;font-size:11px;margin:0 0 12px}
      table{width:100%;border-collapse:collapse}
      th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}
      th{background:#f0f0f0;font-size:11px;text-transform:uppercase}
      .need{color:#b91c1c;font-weight:bold}
      .ok{color:#15803d}
      tr:nth-child(even) td{background:#fafafa}
      tfoot td{font-weight:bold;background:#fef9c3}
      @media print{body{margin:10mm}}
    </style>`;
    const rows = planItems.map((r, i) => `<tr>
      <td>${i + 1}</td>
      <td>${r.name}</td>
      <td>${r.unit}</td>
      <td style="text-align:right">${r.threshold}</td>
      <td style="text-align:right" class="need">+${r.needed}</td>
      <td></td>
    </tr>`).join("");
    const totalNeeded = planItems.reduce((s, r) => s + r.needed, 0);
    const html = `<!DOCTYPE html><html lang="vi"><head><meta charset="UTF-8"/>
      <title>Kế hoạch sản xuất</title>${style}</head><body>
      <h2>Kế hoạch sản xuất — ${kindLabel}</h2>
      <p class="sub">${planItems.length} mục cần bổ sung · Tính theo tồn kho hiện tại · In ngày ${new Date().toLocaleDateString("vi-VN")}</p>
      <p class="sub" style="color:#b91c1c">Công thức: <strong>Cần làm thêm = Ngưỡng tối thiểu − Tồn kho hiện tại</strong></p>
      <table>
        <thead><tr>
          <th>STT</th><th>Tên</th><th>ĐVT</th>
          <th style="text-align:right">Ngưỡng tối thiểu</th>
          <th style="text-align:right">Tồn kho hiện tại</th>
          <th style="text-align:right">Cần làm thêm</th>
          <th>Ghi chú</th>
        </tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr>
          <td colspan="5" style="text-align:right">TỔNG CẦN LÀM THÊM</td>
          <td style="text-align:right;color:#b91c1c">+${totalNeeded}</td>
          <td></td>
        </tr></tfoot>
      </table>
      <script>window.onload=()=>{window.print()}<\/script>
    </body></html>`;
    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
  };
  useEffect(() => { load(); loadForecast(); /* eslint-disable-next-line */ }, []);
  // Filter XNT + movements theo search, áp dụng thứ tự kéo thả
  const xntSorted = xnt?.items
    ? (xntOrder
        ? [...xnt.items].sort((a, b) => {
            const ia = xntOrder.indexOf(String(a.id));
            const ib = xntOrder.indexOf(String(b.id));
            if (ia === -1 && ib === -1) return 0;
            if (ia === -1) return 1;
            if (ib === -1) return -1;
            return ia - ib;
          })
        : xnt.items)
    : [];
  const xntFiltered = xntSorted.filter((r) => !xntSearch.trim() || stripAccents(r.name).includes(stripAccents(xntSearch)));
  const movFiltered = movements.filter((m) =>
    !movSearch.trim() || stripAccents(m.target_name ?? "").includes(stripAccents(movSearch))
  );

  useEffect(() => { if (tab === "xnt" || tab === "plan") loadXNT(); /* eslint-disable-next-line */ }, [tab, xntKind, xntDateFrom, xntDateTo]);
  useEffect(() => { if (tab === "stockin") loadMovements(); /* eslint-disable-next-line */ }, [tab, movKind, movDateFrom, movDateTo]);
  // Sync xntKind ↔ planKind when on plan tab
  useEffect(() => { if (tab === "plan") setXntKind(planKind); /* eslint-disable-next-line */ }, [planKind]);

  // Pre-populate planAllocation từ planItems khi chuyển sang tab mua NVL
  useEffect(() => {
    if (planSubTab === "material_purchase" && dataLoaded && planItems.length > 0) {
      setPlanAllocation(prev => {
        const next = { ...prev };
        for (const item of planItems) {
          if (next[item.id] === undefined && item.needed > 0) {
            next[item.id] = item.needed;
          }
        }
        return next;
      });
    }
    /* eslint-disable-next-line */
  }, [planSubTab, dataLoaded]);

  // Auto-fetch recipe material preview when product or quantity changes in form
  useEffect(() => {
    if (!form.product_id || !open) { setRecipePreview(null); return; }
    let cancelled = false;
    setRecipePreviewLoading(true);
    api.get(`/products/${form.product_id}/recipe/material-estimate?quantity=${form.quantity || 1}`)
      .then(({ data }) => { if (!cancelled) setRecipePreview(data); })
      .catch(() => { if (!cancelled) setRecipePreview(null); })
      .finally(() => { if (!cancelled) setRecipePreviewLoading(false); });
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [form.product_id, form.quantity, open]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.expiration_date) delete payload.expiration_date;
      if (!payload.batch_code) delete payload.batch_code;
      // If no manual materials, backend will auto-use recipe; send empty array
      payload.materials_used = (payload.materials_used || []).filter((m) => m.material_id && m.quantity > 0);
      await api.post("/production", payload);
      toast({ title: "Đã tạo lô sản xuất", variant: "success" });
      setOpen(false);
      setRecipePreview(null);
      load();
      loadForecast();
    } catch (err) {
      toast({ title: parseApiError(err) || t("common.error"), variant: "error" });
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`${t("common.delete")} lô "${b.batch_code}"? Tồn kho sẽ được hoàn lại.`)) return;
    try {
      await api.delete(`/production/${b.batch_id}`);
      toast({ title: t("common.deleted"), variant: "success" });
      load();
    } catch (err) {
      toast({ title: parseApiError(err) || t("common.error"), variant: "error" });
    }
  };

  const addMaterial = () => {
    setForm((p) => ({ ...p, materials_used: [...(p.materials_used || []), { material_id: "", quantity: 0 }] }));
  };

  const updateMaterial = (idx, field, value) => {
    setForm((p) => {
      const next = [...(p.materials_used || [])];
      next[idx] = { ...next[idx], [field]: value };
      return { ...p, materials_used: next };
    });
  };

  const removeMaterial = (idx) => {
    setForm((p) => ({ ...p, materials_used: (p.materials_used || []).filter((_, i) => i !== idx) }));
  };

  // Mở edit movement với PIN2 guard
  const openEditMovement = (m) => {
    const pin = getPin2();
    if (pin) {
      setEditMovModal(m);
      setEditMovForm({
        quantity: m.quantity,
        unit_price: m.unit_price || 0,
        batch_code: m.batch_code || "",
        production_date: m.production_date ? m.production_date.slice(0, 10) : "",
        expiration_date: m.expiration_date ? m.expiration_date.slice(0, 10) : "",
        notes: m.notes || "",
      });
    } else {
      setPendingMovEdit(m);
      setPin2Open(true);
    }
  };

  const onPin2Verified = () => {
    setPin2Open(false);
    if (pendingMovEdit) {
      setEditMovModal(pendingMovEdit);
      setEditMovForm({
        quantity: pendingMovEdit.quantity,
        unit_price: pendingMovEdit.unit_price || 0,
        batch_code: pendingMovEdit.batch_code || "",
        production_date: pendingMovEdit.production_date ? pendingMovEdit.production_date.slice(0, 10) : "",
        expiration_date: pendingMovEdit.expiration_date ? pendingMovEdit.expiration_date.slice(0, 10) : "",
        notes: pendingMovEdit.notes || "",
      });
      setPendingMovEdit(null);
    }
  };

  const submitEditMovement = async (e) => {
    e.preventDefault();
    if (!editMovModal) return;
    setEditMovSubmitting(true);
    try {
      await api.put(`/stock/movements/${editMovModal.movement_id}`, editMovForm);
      toast({ title: "Đã cập nhật phiếu kho", variant: "success" });
      setEditMovModal(null);
      loadMovements();
    } catch (err) {
      toast({ title: parseApiError(err) || t("common.error"), variant: "error" });
    } finally {
      setEditMovSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="production-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
            <Factory size={28} className="text-bamboo" /> Kho hàng hóa
          </h1>
          <p className="text-sm text-ink-secondary mt-1">Nhập kho · Xuất nhập tồn · Sản xuất theo ca · AI gợi ý</p>
        </div>
        {tab === "batches" && (
          <Button onClick={() => setOpen(true)} data-testid="production-new-button">
            <Plus size={16} /> Lô SX mới
          </Button>
        )}
      </div>

      <div className="flex gap-2 border-b border-border overflow-x-auto">
        <button onClick={() => setTab("xnt")} className={cn("flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap", tab === "xnt" ? "border-bamboo text-bamboo" : "border-transparent text-ink-muted")} data-testid="warehouse-tab-xnt">
          <BarChart2 size={14} /> Xuất Nhập Tồn
        </button>
        <button onClick={() => setTab("plan")} className={cn("flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap", tab === "plan" ? "border-bamboo text-bamboo" : "border-transparent text-ink-muted")} data-testid="warehouse-tab-plan">
          <ClipboardList size={14} /> Kế hoạch SX
        </button>
        <button onClick={() => setTab("stockin")} className={cn("flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap", tab === "stockin" ? "border-bamboo text-bamboo" : "border-transparent text-ink-muted")} data-testid="warehouse-tab-stockin">
          <ArrowLeftRight size={14} /> Lịch sử nhập/xuất
        </button>
        <button onClick={() => setTab("batches")} className={cn("flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap", tab === "batches" ? "border-bamboo text-bamboo" : "border-transparent text-ink-muted")} data-testid="production-tab-batches">
          <Calendar size={14} /> Sản xuất theo ca
        </button>
        <button onClick={() => setTab("forecast")} className={cn("flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap", tab === "forecast" ? "border-bamboo text-bamboo" : "border-transparent text-ink-muted")} data-testid="production-tab-forecast">
          <Brain size={14} /> AI Gợi ý
        </button>
      </div>

      {tab === "xnt" && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <CardTitle className="flex items-center gap-2"><BarChart2 size={16} className="text-bamboo" /> Báo cáo Xuất Nhập Tồn</CardTitle>
              <div className="flex flex-wrap gap-2 items-end">
                <div>
                  <Label>Loại</Label>
                  <Select value={xntKind} onChange={(e) => setXntKind(e.target.value)} data-testid="xnt-kind">
                    <option value="product">Sản phẩm</option>
                    <option value="material">Nguyên vật liệu</option>
                  </Select>
                </div>
                <div>
                  <Label>Từ ngày</Label>
                  <Input type="date" value={xntDateFrom} onChange={(e) => setXntDateFrom(e.target.value)} data-testid="xnt-date-from" />
                </div>
                <div>
                  <Label>Đến ngày</Label>
                  <Input type="date" value={xntDateTo} onChange={(e) => setXntDateTo(e.target.value)} data-testid="xnt-date-to" />
                </div>
                <Button variant="outline" size="sm" onClick={exportXntXlsx} data-testid="xnt-export-csv"><Download size={14} /> CSV</Button>
                <Button variant="outline" size="sm" onClick={printXNT}><Printer size={14} /> In</Button>
                {xntOrder && (
                  <Button variant="outline" size="sm" onClick={() => { setXntOrder(null); localStorage.removeItem("xnt_row_order"); }} title="Đặt lại thứ tự mặc định">
                    <ArrowLeftRight size={14} /> Reset
                  </Button>
                )}
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none" />
                  <input
                    type="text"
                    value={xntSearch}
                    onChange={(e) => setXntSearch(e.target.value)}
                    placeholder="Tìm sản phẩm..."
                    className="h-9 pl-8 pr-7 text-sm border border-border rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-bamboo w-44"
                  />
                  {xntSearch && (
                    <button onClick={() => setXntSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink">
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="!p-0">
            {!xnt ? (
              <div className="text-center py-8 text-ink-muted text-sm">Đang tải...</div>
            ) : (
              <div className="overflow-x-auto overflow-y-auto max-h-[55vh]">
                {/* KPI CARDS TỔNG NHANH — kiểu gradient như dashboard */}
                {xnt.items.length > 0 && (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4 border-b border-border print:hidden">
                    {/* Tồn đầu */}
                    <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#52525b] to-[#71717a] shadow-[0_6px_20px_rgba(82,82,91,0.3)] p-4">
                      <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-white/10 pointer-events-none" />
                      <div className="text-xs uppercase tracking-widest font-semibold text-white/65 mb-1">Tồn đầu</div>
                      <div className="font-heading text-2xl font-bold text-white leading-none">{xnt.totals.total_opening}</div>
                    </div>
                    {/* Nhập */}
                    <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#15803d] to-[#22c55e] shadow-[0_6px_20px_rgba(21,128,61,0.3)] p-4">
                      <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-white/10 pointer-events-none" />
                      <div className="text-xs uppercase tracking-widest font-semibold text-white/65 mb-1">Tổng nhập</div>
                      <div className="font-heading text-2xl font-bold text-white leading-none">+{xnt.totals.total_in}</div>
                    </div>
                    {/* Xuất */}
                    <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#b91c1c] to-[#ef4444] shadow-[0_6px_20px_rgba(185,28,28,0.3)] p-4">
                      <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-white/10 pointer-events-none" />
                      <div className="text-xs uppercase tracking-widest font-semibold text-white/65 mb-1">Tổng xuất</div>
                      <div className="font-heading text-2xl font-bold text-white leading-none">-{xnt.totals.total_out}</div>
                    </div>
                    {/* Tồn cuối */}
                    <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#2D4A22] to-[#4a7a38] shadow-[0_6px_20px_rgba(45,74,34,0.35)] p-4">
                      <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-white/10 pointer-events-none" />
                      <div className="text-xs uppercase tracking-widest font-semibold text-white/65 mb-1">Tồn cuối</div>
                      <div className="font-heading text-2xl font-bold text-white leading-none">{xnt.totals.total_ending}</div>
                    </div>
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-cream/40 text-xs uppercase text-ink-muted sticky top-0 z-10">
                    <tr>
                      <th className="py-3 px-2 bg-[#f5f0e8] w-8" title="Kéo để sắp xếp lại"></th>
                      <th className="text-left py-3 px-4 bg-[#f5f0e8]">Tên</th>
                      <th className="text-center py-3 px-4 bg-[#f5f0e8]">ĐVT</th>
                      <th className="text-right py-3 px-4 bg-[#f5f0e8]">Tồn đầu</th>
                      <th className="text-right py-3 px-4 bg-[#f5f0e8] text-green-700">Nhập</th>
                      <th className="text-right py-3 px-4 bg-[#f5f0e8] text-red-600">Xuất</th>
                      <th className="text-right py-3 px-4 bg-[#f5f0e8]">Tồn cuối</th>
                    </tr>
                  </thead>
                  <tbody data-testid="xnt-table-body">
                    {xntFiltered.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-12 text-ink-muted">{xntSearch ? "Không tìm thấy sản phẩm nào" : "Không có dữ liệu"}</td></tr>
                    ) : xntFiltered.map((r, idx) => (
                      <tr
                        key={r.id}
                        className={cn("border-t border-border hover:bg-cream/30 transition-colors", dragSrcIdx === idx && "opacity-40 bg-bamboo/10")}
                        data-testid={`xnt-row-${r.id}`}
                        draggable={!xntSearch}
                        onDragStart={() => setDragSrcIdx(idx)}
                        onDragEnd={() => setDragSrcIdx(null)}
                        onDragOver={(e) => { e.preventDefault(); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (dragSrcIdx === null || dragSrcIdx === idx) return;
                          const newOrder = [...xntFiltered];
                          const [moved] = newOrder.splice(dragSrcIdx, 1);
                          newOrder.splice(idx, 0, moved);
                          const ids = newOrder.map((x) => String(x.id));
                          setXntOrder(ids);
                          localStorage.setItem("xnt_row_order", JSON.stringify(ids));
                          setDragSrcIdx(null);
                        }}
                      >
                        <td className="py-3 px-2 text-center">
                          {!xntSearch && (
                            <span className="cursor-grab active:cursor-grabbing text-ink-muted/40 hover:text-ink-muted inline-flex">
                              <GripVertical size={14} />
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 font-medium">{r.name}</td>
                        <td className="py-3 px-4 text-center text-xs">{r.unit}</td>
                        <td className="py-3 px-4 text-right font-mono">{r.opening_stock}</td>
                        <td className="py-3 px-4 text-right font-mono text-green-700">+{r.total_in}</td>
                        <td className="py-3 px-4 text-right font-mono text-red-600">-{r.total_out}</td>
                        <td className="py-3 px-4 text-right font-mono font-bold">{r.ending_stock}</td>
                      </tr>
                    ))}
                    {xntFiltered.length > 0 && !xntSearch && (
                      <tr className="border-t-2 border-bamboo bg-cream/60 font-bold">
                        <td></td>
                        <td className="py-3 px-4">TỔNG</td>
                        <td></td>
                        <td className="py-3 px-4 text-right font-mono">{xnt.totals.total_opening}</td>
                        <td className="py-3 px-4 text-right font-mono text-green-700">+{xnt.totals.total_in}</td>
                        <td className="py-3 px-4 text-right font-mono text-red-600">-{xnt.totals.total_out}</td>
                        <td className="py-3 px-4 text-right font-mono">{xnt.totals.total_ending}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── KẾ HOẠCH SẢN XUẤT ─────────────────────────────────────────────── */}
      {tab === "plan" && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <CardTitle className="flex items-center gap-2">
                <ClipboardList size={16} className="text-bamboo" /> Kế hoạch sản xuất
              </CardTitle>
              <div className="flex flex-wrap gap-2 items-end">
                {planSubTab === "product" && (
                  <Button variant="outline" size="sm" onClick={printPlan} disabled={!planItems.length} data-testid="plan-print">
                    <Printer size={14} /> In kế hoạch SP
                  </Button>
                )}
                {planSubTab === "material_purchase" && (
                  <Button variant="outline" size="sm" onClick={printMaterialPurchasePlan} disabled={!materialPurchasePlan.length}>
                    <Printer size={14} /> In kế hoạch mua NVL
                  </Button>
                )}
              </div>
            </div>
            {/* Sub-tabs */}
            <div className="flex gap-1 mt-3 border-b border-border">
              <button
                onClick={() => setPlanSubTab("product")}
                className={cn("flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px", planSubTab === "product" ? "border-bamboo text-bamboo" : "border-transparent text-ink-muted hover:text-ink")}
              >
                <ClipboardList size={13} /> Kế hoạch SX sản phẩm
              </button>
              <button
                onClick={() => setPlanSubTab("material_purchase")}
                className={cn("flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px", planSubTab === "material_purchase" ? "border-bamboo text-bamboo" : "border-transparent text-ink-muted hover:text-ink")}
              >
                <ShoppingCart size={13} /> Kế hoạch mua NVL
                {materialPurchasePlan.length > 0 && (
                  <span className="ml-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{materialPurchasePlan.length}</span>
                )}
              </button>
            </div>
            {planSubTab === "product" && (
              <p className="text-xs text-ink-muted mt-2">
                Công thức: <strong className="text-red-700">Cần làm thêm = Ngưỡng tối thiểu − Tồn kho hiện tại</strong>
              </p>
            )}
            {planSubTab === "material_purchase" && (
              <p className="text-xs text-ink-muted mt-2">
                Nhập số lượng bánh <strong>dự kiến sản xuất</strong> cho từng sản phẩm → hệ thống tự tính NVL cần mua theo công thức: <strong className="text-red-700">Cần mua = (Định lượng recipe × SL kế hoạch SX) − Tồn NVL hiện tại</strong>
              </p>
            )}
          </CardHeader>
          <CardContent className="!p-0">
            {!dataLoaded ? (
              <div className="text-center py-8 text-ink-muted text-sm animate-pulse">Đang tải dữ liệu...</div>
            ) : planSubTab === "product" ? (
              /* ── Sub-tab: Kế hoạch sản xuất sản phẩm ── */
              planItems.length === 0 ? (
                <div className="text-center py-12 text-ink-muted text-sm">
                  <ClipboardList size={36} className="mx-auto mb-3 opacity-20" />
                  <p>Chưa có sản phẩm nào.</p>
                  <p className="text-xs mt-1">Thêm sản phẩm trong trang Sản phẩm để bắt đầu lập kế hoạch.</p>
                </div>
              ) : (
                <div className="overflow-x-auto overflow-y-auto max-h-[55vh]">
                  <div className="grid grid-cols-2 gap-3 p-4 border-b border-border print:hidden">
                    <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#b91c1c] to-[#ef4444] shadow-md p-4 text-white">
                      <div className="absolute -top-3 -right-3 w-16 h-16 rounded-full bg-white/10 pointer-events-none" />
                      <div className="text-[11px] uppercase tracking-widest font-semibold text-white/70 mb-1">SP cần bổ sung</div>
                      <div className="font-heading text-3xl font-bold">{planItems.filter(r => r.isLow).length}</div>
                    </div>
                    <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#92400e] to-[#f59e0b] shadow-md p-4 text-white">
                      <div className="absolute -top-3 -right-3 w-16 h-16 rounded-full bg-white/10 pointer-events-none" />
                      <div className="text-[11px] uppercase tracking-widest font-semibold text-white/70 mb-1">Tổng cần làm thêm</div>
                      <div className="font-heading text-3xl font-bold">{planItems.reduce((s, r) => s + r.needed, 0)}</div>
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-cream/40 text-xs uppercase text-ink-muted sticky top-0 z-10">
                      <tr>
                        <th className="text-left py-3 px-4 bg-[#f5f0e8]">Tên sản phẩm</th>
                        <th className="text-center py-3 px-4 bg-[#f5f0e8]">ĐVT</th>
                        <th className="text-right py-3 px-4 bg-[#f5f0e8]">Ngưỡng tối thiểu</th>
  
                        <th className="text-right py-3 px-4 bg-[#f5f0e8] text-red-700 font-bold">Cần làm thêm</th>
                        <th className="text-center py-3 px-4 bg-[#f5f0e8]">Trạng thái</th>
                      </tr>
                    </thead>
                    <tbody data-testid="plan-table-body">
                      {planItems.map((r) => (
                        <tr key={r.id} className={`border-t border-border ${r.isLow ? "bg-red-50/40 hover:bg-red-50/60" : "hover:bg-cream/40"}`} data-testid={`plan-row-${r.id}`}>
                          <td className="py-3 px-4 font-medium">{r.name}</td>
                          <td className="py-3 px-4 text-center text-xs">{r.unit}</td>
                          <td className="py-3 px-4 text-right font-mono text-ink-muted">{r.threshold || "—"}</td>
                          <td className="py-3 px-4 text-right">
                            {r.needed > 0 ? (
                              <span className="inline-flex items-center gap-1 bg-red-50 border border-red-200 text-red-700 font-bold font-mono px-2 py-0.5 rounded-full text-xs">+{r.needed}</span>
                            ) : (
                              <span className="text-xs text-ink-muted">—</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {r.threshold <= 0 ? (
                              <span className="text-xs text-ink-muted bg-gray-100 px-2 py-0.5 rounded-full">Chưa đặt ngưỡng</span>
                            ) : r.isLow ? (
                              <span className="text-xs text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full font-medium">⚠ Thiếu hàng</span>
                            ) : (
                              <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full font-medium">✓ Đủ hàng</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-bamboo bg-cream/60 font-bold">
                        <td className="py-3 px-4" colSpan={4}>TỔNG CẦN LÀM THÊM</td>
                        <td className="py-3 px-4 text-right">
                          <span className="inline-flex items-center gap-1 bg-red-100 border border-red-300 text-red-800 font-bold font-mono px-3 py-0.5 rounded-full text-sm">
                            +{planItems.reduce((s, r) => s + r.needed, 0)}
                          </span>
                        </td>
                        <td />
                      </tr>
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              /* ── Sub-tab: Kế hoạch mua NVL ── */
              <div className="overflow-x-auto overflow-y-auto max-h-[55vh]">
                {/* ── Nhập số lượng kế hoạch SX ── */}
                <div className="p-4 border-b border-border bg-amber-50/40">
                  <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-2">Số lượng kế hoạch sản xuất (bánh dự kiến làm)</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    {products.map(p => {
                      const hasRecipe = (recipes[p.product_id] || p.recipe || []).length > 0;
                      const val = planAllocation[p.product_id];
                      return (
                        <div key={p.product_id} className="flex flex-col gap-0.5">
                          <label className="text-[11px] font-medium truncate" title={p.name} style={{color: hasRecipe ? undefined : '#b45309'}}>
                            {p.name}{!hasRecipe && <span className="ml-1 text-[10px]">⚠ chưa có CT</span>}
                          </label>
                          <div className="flex items-center gap-1">
                            <Input
                              type="number" min={0} step="any"
                              value={val === undefined ? "" : val}
                              placeholder="0"
                              onChange={(e) => setPlanAllocation(prev => ({ ...prev, [p.product_id]: e.target.value === "" ? undefined : Number(e.target.value) }))}
                              className="text-right text-sm"
                            />
                            <span className="text-[11px] text-ink-muted shrink-0">{p.unit || "cái"}</span>
                          </div>
                        </div>
                      );
                    })}
                    {products.length === 0 && (
                      <div className="col-span-4 text-xs text-amber-700">Chưa có sản phẩm nào.</div>
                    )}
                  </div>
                  {Object.keys(planAllocation).length > 0 && (
                    <button
                      className="mt-2 text-xs text-ink-muted underline hover:text-red-600"
                      onClick={() => setPlanAllocation({})}
                    >Xóa tất cả / reset về kế hoạch SX</button>
                  )}
                </div>

                {materialPurchasePlan.length === 0 ? (
                  <div className="text-center py-10 text-ink-muted text-sm">
                    <ShoppingCart size={28} className="mx-auto mb-2 opacity-20" />
                    <p>Nhập số lượng dự kiến SX bên trên để xem NVL cần mua.</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-3 p-4 border-b border-border print:hidden">
                      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#b91c1c] to-[#ef4444] shadow-md p-4 text-white">
                        <div className="absolute -top-3 -right-3 w-16 h-16 rounded-full bg-white/10 pointer-events-none" />
                        <div className="text-[11px] uppercase tracking-widest font-semibold text-white/70 mb-1">Loại NVL cần mua</div>
                        <div className="font-heading text-3xl font-bold">{materialPurchasePlan.filter(r => r.must_buy > 0).length}</div>
                      </div>
                      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#92400e] to-[#f59e0b] shadow-md p-4 text-white">
                        <div className="absolute -top-3 -right-3 w-16 h-16 rounded-full bg-white/10 pointer-events-none" />
                        <div className="text-[11px] uppercase tracking-widest font-semibold text-white/70 mb-1">Tổng cần cho SX</div>
                        <div className="font-heading text-3xl font-bold">{materialPurchasePlan.reduce((s, r) => s + r.needed_for_production, 0)}</div>
                      </div>
                      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#1e40af] to-[#3b82f6] shadow-md p-4 text-white">
                        <div className="absolute -top-3 -right-3 w-16 h-16 rounded-full bg-white/10 pointer-events-none" />
                        <div className="text-[11px] uppercase tracking-widest font-semibold text-white/70 mb-1">Dự tính chi phí</div>
                        <div className="text-sm font-bold mt-1">{formatVND(materialPurchasePlan.reduce((s, r) => s + r.total_cost, 0))}</div>
                      </div>
                    </div>
                    <table className="w-full text-sm">
                      <thead className="bg-cream/40 text-xs uppercase text-ink-muted sticky top-0 z-10">
                        <tr>
                          <th className="text-left py-3 px-4 bg-[#f5f0e8]">Tên NVL</th>
                          <th className="text-center py-3 px-4 bg-[#f5f0e8]">ĐVT</th>
    
                          <th className="text-right py-3 px-4 bg-[#f5f0e8]">Cần cho SX</th>
                          <th className="text-right py-3 px-4 bg-[#f5f0e8] text-red-700 font-bold">Cần mua thêm</th>
                          <th className="text-right py-3 px-4 bg-[#f5f0e8]">Dự tính chi phí</th>
                          <th className="text-left py-3 px-4 bg-[#f5f0e8]">Phân bổ từ SP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {materialPurchasePlan.map((r) => (
                          <tr key={r.material_id} className={`border-t border-border ${r.must_buy > 0 ? "bg-amber-50/30 hover:bg-amber-50/50" : "hover:bg-cream/40"}`}>
                            <td className="py-3 px-4 font-medium">{r.name}</td>
                            <td className="py-3 px-4 text-center text-xs">{r.unit}</td>
                            <td className="py-3 px-4 text-right font-mono text-amber-700 font-semibold">{r.needed_for_production}</td>
                            <td className="py-3 px-4 text-right">
                              {r.must_buy > 0 ? (
                                <span className="inline-flex items-center gap-1 bg-red-50 border border-red-200 text-red-700 font-bold font-mono px-2 py-0.5 rounded-full text-xs">+{r.must_buy}</span>
                              ) : (
                                <span className="text-xs text-green-700 font-medium">✓ Đủ</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-right font-mono text-xs">{r.total_cost > 0 ? formatVND(r.total_cost) : "—"}</td>
                            <td className="py-3 px-4 text-xs text-ink-muted">
                              {r.sources.map((s, i) => (
                                <div key={i}>{s.product_name}: {s.qty_per_unit}×{s.planned_qty}={s.total_qty}</div>
                              ))}
                            </td>
                          </tr>
                        ))}
                        <tr className="border-t-2 border-bamboo bg-cream/60 font-bold">
                          <td className="py-3 px-4" colSpan={4}>TỔNG</td>
                          <td className="py-3 px-4 text-right">
                            <span className="inline-flex items-center gap-1 bg-red-100 border border-red-300 text-red-800 font-bold font-mono px-3 py-0.5 rounded-full text-sm">
                              +{materialPurchasePlan.reduce((s, r) => s + r.must_buy, 0)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right font-mono">{formatVND(materialPurchasePlan.reduce((s, r) => s + r.total_cost, 0))}</td>
                          <td />
                        </tr>
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "stockin" && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <CardTitle className="flex items-center gap-2"><ArrowLeftRight size={16} className="text-bamboo" /> Lịch sử nhập/xuất kho</CardTitle>
              <div className="flex flex-wrap gap-2 items-end">
                <div>
                  <Label>Loại</Label>
                  <Select value={movKind} onChange={(e) => setMovKind(e.target.value)} data-testid="mov-kind">
                    <option value="">Tất cả</option>
                    <option value="product">Sản phẩm</option>
                    <option value="material">Nguyên vật liệu</option>
                  </Select>
                </div>
                <div>
                  <Label>Từ ngày</Label>
                  <Input type="date" value={movDateFrom} onChange={(e) => setMovDateFrom(e.target.value)} data-testid="mov-date-from" />
                </div>
                <div>
                  <Label>Đến ngày</Label>
                  <Input type="date" value={movDateTo} onChange={(e) => setMovDateTo(e.target.value)} data-testid="mov-date-to" />
                </div>
                <Button variant="outline" size="sm" onClick={printMovements}><Printer size={14} /> In</Button>
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none" />
                  <input
                    type="text"
                    value={movSearch}
                    onChange={(e) => setMovSearch(e.target.value)}
                    placeholder="Tìm sản phẩm / NVL..."
                    className="h-9 pl-8 pr-7 text-sm border border-border rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-bamboo w-48"
                  />
                  {movSearch && (
                    <button onClick={() => setMovSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink">
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="!p-0">
            <div className="overflow-x-auto overflow-y-auto max-h-[55vh]">
              <table className="w-full text-sm">
                <thead className="bg-cream/40 text-xs uppercase text-ink-muted sticky top-0 z-10">
                  <tr>
                    <th className="text-left py-3 px-4 bg-[#f5f0e8]">Thời gian</th>
                    <th className="text-left py-3 px-4 bg-[#f5f0e8]">Loại</th>
                    <th className="text-left py-3 px-4 bg-[#f5f0e8]">Đối tượng</th>
                    <th className="text-center py-3 px-4 bg-[#f5f0e8]">Hoạt động</th>
                    <th className="text-right py-3 px-4 bg-[#f5f0e8]">SL</th>
                    <th className="text-right py-3 px-4 bg-[#f5f0e8]">Đơn giá</th>
                    <th className="text-left py-3 px-4 bg-[#f5f0e8]">Lô / HSD</th>
                    <th className="text-left py-3 px-4 bg-[#f5f0e8]">Ghi chú</th>
                    <th className="py-3 px-4 bg-[#f5f0e8]"></th>
                  </tr>
                </thead>
                <tbody data-testid="mov-table-body">
                  {movFiltered.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-12 text-ink-muted">{movSearch ? "Không tìm thấy" : "Không có dữ liệu"}</td></tr>
                  ) : movFiltered.map((m) => (
                    <tr key={m.movement_id} className="border-t border-border hover:bg-cream/30" data-testid={`mov-row-${m.movement_id}`}>
                      <td className="py-3 px-4 text-xs">{formatDateTime(m.created_at)}</td>
                      <td className="py-3 px-4 text-xs">{m.kind === "product" ? "SP" : "NVL"}</td>
                      <td className="py-3 px-4 font-medium">{m.target_name}</td>
                      <td className="py-3 px-4 text-center">
                        <Badge variant={m.quantity_change > 0 ? "delivered" : "cancelled"}>{m.type || (m.quantity_change > 0 ? "nhập" : "xuất")}</Badge>
                      </td>
                      <td className={cn("py-3 px-4 text-right font-mono", m.quantity_change > 0 ? "text-green-700" : "text-red-600")}>
                        {m.quantity_change > 0 ? "+" : ""}{m.quantity_change}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-xs">{m.unit_price ? formatVND(m.unit_price) : "—"}</td>
                      <td className="py-3 px-4 text-xs">{m.batch_code || ""} {m.expiration_date ? `· HSD ${formatDate(m.expiration_date)}` : ""}</td>
                      <td className="py-3 px-4 text-xs text-ink-muted">{m.notes || ""}</td>
                      <td className="py-3 px-4 text-right">
                        {m.type === "in" && (
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => openEditMovement(m)}
                            title="Sửa phiếu nhập (yêu cầu mật khẩu cấp 2)"
                            className="relative"
                            data-testid={`mov-edit-${m.movement_id}`}
                          >
                            <Edit size={14} />
                            <ShieldCheck size={8} className="absolute bottom-0.5 right-0.5 text-amber-500" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {tab === "batches" && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-cream/40 text-xs uppercase text-ink-muted">
                <tr>
                  <th className="text-left py-3 px-4">Mã lô</th>
                  <th className="text-left py-3 px-4">Sản phẩm</th>
                  <th className="text-center py-3 px-4">Ca</th>
                  <th className="text-right py-3 px-4">SL</th>
                  <th className="text-left py-3 px-4">Ngày SX</th>
                  <th className="text-left py-3 px-4">HSD</th>
                  <th className="text-left py-3 px-4">Người tạo</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody data-testid="production-table-body">
                {batches.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-12 text-ink-muted">Chưa có lô sản xuất</td></tr>
                ) : batches.map((b) => (
                  <tr key={b.batch_id} className="border-t border-border hover:bg-cream/30" data-testid={`production-row-${b.batch_id}`}>
                    <td className="py-3 px-4 font-mono text-xs">{b.batch_code}</td>
                    <td className="py-3 px-4 font-medium">{b.product_name}</td>
                    <td className="py-3 px-4 text-center"><Badge variant="regular">{SHIFTS.find((s) => s.id === b.shift)?.label || b.shift}</Badge></td>
                    <td className="py-3 px-4 text-right font-mono">{b.quantity}</td>
                    <td className="py-3 px-4 text-xs">{formatDate(b.production_date)}</td>
                    <td className="py-3 px-4 text-xs">{formatDate(b.expiration_date)}</td>
                    <td className="py-3 px-4 text-xs text-ink-muted">{b.created_by}</td>
                    <td className="py-3 px-4 text-right">
                      <Button variant="ghost" size="icon" onClick={() => remove(b)} data-testid={`production-delete-${b.batch_id}`}>
                        <Trash2 size={14} className="text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === "forecast" && (
        <div className="space-y-4">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="flex items-center gap-2"><Sparkles size={16} className="text-terracotta" /> Gợi ý sản xuất {forecast?.target_day_of_week ? `cho ${forecast.target_day_of_week} (${forecast.target_date})` : ""}</CardTitle>
              <Button variant="outline" size="sm" onClick={loadForecast} data-testid="forecast-refresh">Tải lại</Button>
            </div>
          </CardHeader>
          <CardContent className="!p-0">
            {!forecast ? (
              <div className="text-center py-8 text-ink-muted text-sm">Đang tải...</div>
            ) : forecast.forecasts.length === 0 ? (
              <div className="text-center py-8 text-ink-muted text-sm">
                Chưa đủ dữ liệu (cần ≥1 đơn hàng trong 28 ngày qua cùng thứ trong tuần). Hệ thống AI sẽ học khi có nhiều data hơn.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-cream/40 text-xs uppercase text-ink-muted">
                  <tr>
                    <th className="text-left py-3 px-4">Sản phẩm</th>
                    <th className="text-right py-3 px-4">TB bán/ngày</th>
                    <th className="text-right py-3 px-4">Tồn hiện tại</th>
                    <th className="text-right py-3 px-4">Gợi ý sản xuất</th>
                    <th className="text-right py-3 px-4">Cần làm thêm</th>
                  </tr>
                </thead>
                <tbody>
                  {forecast.forecasts.map((f) => (
                    <tr key={f.product_id} className="border-t border-border" data-testid={`forecast-row-${f.product_id}`}>
                      <td className="py-3 px-4 font-medium">{f.name}</td>
                      <td className="py-3 px-4 text-right text-xs">{f.avg_daily_sold} ({f.samples} mẫu)</td>
                      <td className="py-3 px-4 text-right">{f.current_stock} {f.unit}</td>
                      <td className="py-3 px-4 text-right font-bold text-bamboo">{f.recommended_production} {f.unit}</td>
                      <td className="py-3 px-4 text-right">
                        {f.needs_to_produce > 0 ? (
                          <span className="font-bold text-terracotta">{f.needs_to_produce} {f.unit}</span>
                        ) : (
                          <Badge variant="delivered">Đủ</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Material estimate for tomorrow's forecast */}
        {forecast && forecast.material_estimate && forecast.material_estimate.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span>🧪</span> Dự tính NVL cần chuẩn bị cho ngày mai
              </CardTitle>
              <p className="text-xs text-ink-muted mt-1">Tổng hợp từ công thức của tất cả bánh cần làm thêm. Bánh chưa có công thức sẽ không tính.</p>
            </CardHeader>
            <CardContent className="!p-0">
              <table className="w-full text-sm">
                <thead className="bg-cream/40 text-xs uppercase text-ink-muted">
                  <tr>
                    <th className="text-left py-3 px-4">Nguyên vật liệu</th>
                    <th className="text-right py-3 px-4">Cần dùng</th>
                    <th className="text-right py-3 px-4">Tồn kho hiện tại</th>
                    <th className="text-right py-3 px-4">Trạng thái</th>
                  </tr>
                </thead>
                <tbody>
                  {forecast.material_estimate.map((m) => (
                    <tr key={m.material_id} className="border-t border-border">
                      <td className="py-3 px-4 font-medium">{m.material_name}</td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-purple-700">{m.needed_total.toFixed(3).replace(/\.?0+$/, "")} {m.unit}</td>
                      <td className="py-3 px-4 text-right text-ink-muted">{m.current_stock} {m.unit}</td>
                      <td className="py-3 px-4 text-right">
                        {m.sufficient ? (
                          <Badge variant="delivered">✓ Đủ</Badge>
                        ) : (
                          <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded">⚠ Thiếu {m.shortage.toFixed(3).replace(/\.?0+$/, "")} {m.unit}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {forecast && forecast.forecasts.length > 0 && (!forecast.material_estimate || forecast.material_estimate.length === 0) && (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            💡 <strong>Chưa có dự tính NVL.</strong> Vào trang <strong>Sản phẩm</strong> → nút <strong>🧪</strong> để thiết lập công thức nguyên vật liệu cho từng loại bánh. Khi có công thức, hệ thống sẽ tự tính NVL cần chuẩn bị cho ngày mai.
          </div>
        )}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Tạo lô sản xuất" size="lg" testId="production-modal">
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Sản phẩm *</Label>
              <Select value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} required data-testid="production-form-product">
                <option value="">— Chọn sản phẩm —</option>
                {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
              </Select>
            </div>
            <div>
              <Label>Số lượng *</Label>
              <Input type="number" min={1} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} required data-testid="production-form-qty" />
            </div>
            <div>
              <Label>Ca sản xuất</Label>
              <Select value={form.shift} onChange={(e) => setForm({ ...form, shift: e.target.value })}>
                {SHIFTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </Select>
            </div>
            <div>
              <Label>Mã lô</Label>
              <Input value={form.batch_code} onChange={(e) => setForm({ ...form, batch_code: e.target.value })} placeholder="Tự sinh nếu để trống" />
            </div>
            <div>
              <Label>Ngày sản xuất</Label>
              <Input type="date" value={form.production_date} onChange={(e) => setForm({ ...form, production_date: e.target.value })} data-testid="production-form-prod-date" />
            </div>
            <div>
              <Label>HSD</Label>
              <Input type="date" value={form.expiration_date} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} placeholder="Tự tính từ HSD mặc định" />
            </div>
          </div>

          {/* Recipe auto-preview */}
          {recipePreview && recipePreview.estimate && recipePreview.estimate.length > 0 && (
            <div className="border border-purple-200 bg-purple-50 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-purple-800">
                <span>🧪</span> NVL tự động theo công thức ({form.quantity} {recipePreview.product_name && `× ${recipePreview.product_name}`})
              </div>
              <div className="space-y-1">
                {recipePreview.estimate.map((item) => (
                  <div key={item.material_id} className="flex items-center justify-between text-xs rounded px-2 py-1 bg-white border border-purple-100">
                    <span className="font-medium text-ink">{item.material_name}</span>
                    <span className="flex items-center gap-2">
                      <span className="text-purple-700 font-mono">cần {item.needed_total} {item.unit}</span>
                      <span className={item.sufficient ? "text-green-600" : "text-red-600 font-semibold"}>
                        {item.sufficient ? `✓ còn ${item.current_stock}` : `⚠ thiếu ${item.shortage} ${item.unit}`}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-purple-600">NVL sẽ tự động trừ khi lưu lô sản xuất. Nhập thủ công bên dưới để ghi đè.</p>
            </div>
          )}
          {recipePreviewLoading && (
            <div className="text-xs text-ink-muted animate-pulse py-1">Đang tính NVL theo công thức...</div>
          )}
          {recipePreview && (!recipePreview.estimate || recipePreview.estimate.length === 0) && form.product_id && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              ⚠ Sản phẩm này chưa có công thức NVL. Vào trang <strong>Sản phẩm</strong> để thiết lập công thức, hoặc nhập NVL thủ công bên dưới.
            </div>
          )}

          {/* Manual materials override */}
          <div className="border-t border-border pt-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-ink-muted">NVL nhập thủ công (ghi đè công thức)</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addMaterial}>+ Thêm NVL</Button>
            </div>
            {(form.materials_used || []).map((m, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 mb-2" data-testid={`production-material-${i}`}>
                <div className="col-span-7">
                  <Select value={m.material_id} onChange={(e) => updateMaterial(i, "material_id", e.target.value)}>
                    <option value="">— Chọn NVL —</option>
                    {materials.map((mat) => <option key={mat.material_id} value={mat.material_id}>{mat.name} (còn {mat.stock} {mat.unit})</option>)}
                  </Select>
                </div>
                <div className="col-span-4">
                  <Input type="number" min={0} step="any" value={m.quantity} onChange={(e) => updateMaterial(i, "quantity", Number(e.target.value))} placeholder="Số lượng dùng" />
                </div>
                <div className="col-span-1">
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeMaterial(i)}>
                    <Trash2 size={14} className="text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div>
            <Label>{t("common.notes")}</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" type="button" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button type="submit" data-testid="production-form-submit">{t("common.save")}</Button>
          </div>
        </form>
      </Modal>
      {/* PIN2 prompt cho sửa phiếu kho */}
      <Pin2Prompt
        open={pin2Open}
        onClose={() => { setPin2Open(false); setPendingMovEdit(null); }}
        onVerified={onPin2Verified}
        action="chỉnh sửa phiếu nhập/xuất kho"
      />

      {/* Modal sửa phiếu nhập kho */}
      <Modal
        open={!!editMovModal}
        onClose={() => setEditMovModal(null)}
        title={`Sửa phiếu: ${editMovModal?.target_name || ""} — ${editMovModal?.batch_code || editMovModal?.movement_id || ""}`}
        size="md"
        testId="edit-mov-modal"
      >
        <form onSubmit={submitEditMovement} className="space-y-4">
          {/* thông tin gốc */}
          <div className="bg-cream border border-border rounded-lg p-3 text-xs text-ink-muted space-y-1">
            <div className="flex justify-between">
              <span>Phiếu:</span>
              <span className="font-mono">{editMovModal?.movement_id}</span>
            </div>
            <div className="flex justify-between">
              <span>Loại:</span>
              <span>{editMovModal?.kind === "material" ? "Nguyên vật liệu" : "Sản phẩm"}</span>
            </div>
            <div className="flex justify-between">
              <span>Người tạo:</span>
              <span>{editMovModal?.created_by}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Số lượng *</Label>
              <Input
                type="number" min={0} step="any" required
                value={editMovForm.quantity}
                onChange={(e) => setEditMovForm({ ...editMovForm, quantity: Number(e.target.value) })}
                data-testid="edit-mov-qty"
              />
            </div>
            <div>
              <Label>Đơn giá</Label>
              <Input
                type="number" min={0}
                value={editMovForm.unit_price}
                onChange={(e) => setEditMovForm({ ...editMovForm, unit_price: Number(e.target.value) })}
                data-testid="edit-mov-price"
              />
            </div>
            <div>
              <Label>Mã lô</Label>
              <Input
                value={editMovForm.batch_code}
                onChange={(e) => setEditMovForm({ ...editMovForm, batch_code: e.target.value })}
                data-testid="edit-mov-batch"
              />
            </div>
            <div>
              <Label>Ngày sản xuất</Label>
              <Input
                type="date"
                value={editMovForm.production_date}
                onChange={(e) => setEditMovForm({ ...editMovForm, production_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Hạn sử dụng</Label>
              <Input
                type="date"
                value={editMovForm.expiration_date}
                onChange={(e) => setEditMovForm({ ...editMovForm, expiration_date: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label>Ghi chú</Label>
            <Input
              value={editMovForm.notes}
              onChange={(e) => setEditMovForm({ ...editMovForm, notes: e.target.value })}
              data-testid="edit-mov-notes"
            />
          </div>

          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex gap-2">
            <ShieldCheck size={14} className="shrink-0 mt-0.5" />
            <span>Thay đổi số lượng sẽ tự động điều chỉnh tồn kho. Chỉ sửa khi thực sự cần thiết.</span>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" type="button" onClick={() => setEditMovModal(null)}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={editMovSubmitting} data-testid="edit-mov-submit">
              {editMovSubmitting ? "Đang lưu..." : t("common.save")}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}